/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { FormInputWrapper } from "@eisinc/MobiusOverrides/FormField";
import { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { css } from "styled-components";

export const styledScrollbar = (width = 11, color?: string) => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: #eeeeee;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
            border-radius: 6px;
            border: 3px solid transparent;
        }
    `;
};

export const styledScrollbarHorizontal = (width = 3, color?: string) => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${color ? getColor(color) : resolveColor("common.ui8", theme)};
        overflow-x: auto;

        &::-webkit-scrollbar {
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
            border-radius: 6px;
            border: 1px solid transparent;
        }
    `;
};

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}
        padding: 10px 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 3px;
        ${StyledClickable} {
            ${/* sc-selector */ StyledTypography} {
                font-size: 14px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.background", theme)};
                color: ${resolveColor("text", theme)};
                text-decoration: underline;
            }
        }
    `;
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

export const styledSlider = () => {
    return ({ theme }: any) => css`
        position: relative;
        .swiper-container {
            width: 100% !important;
        }
        .swiper-carousel {
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            z-index: 0;
        }
        .swiper-wrapper {
            box-sizing: border-box;
            z-index: 0;
        }

        .swiper-navigation {
            z-index: 1;
            > div {
                color: ${getColor("secondary")};
                > svg {
                    width: 30px;
                    height: 30px;
                }
            }
            .swiper-button-prev {
                z-index: 0;
                left: -25px;
                @media screen and (max-width: 1024px) {
                    left: -20px;
                }
                @media screen and (max-width: 768px) {
                    left: -5px;
                }
                &:after {
                    content: none;
                }
            }

            .swiper-button-next {
                z-index: 0;
                right: -25px;
                @media screen and (max-width: 1024px) {
                    right: -17px;
                }
                @media screen and (max-width: 768px) {
                    right: -5px;
                }
                &:after {
                    content: none;
                }
            }
        }

        .swiper-pagination-bullet {
            height: 19px;
            width: 19px;
            margin-right: 54px !important;
            &:last-child {
                margin-right: 54px !important;
            }
            background: ${getColor("common.ui3")};
            opacity: 1;
        }
        .swiper-pagination-bullet-active {
            background: ${getColor("primary.main")};
        }

        .swiper-button-disabled {
            display: none;
        }

        .swiper-slide {
            width: 100%;
            padding: 5px 15px;
            height: auto;
            min-height: 100%;

            @media (max-width: 768px) {
                display: flex;
                justify-content: center;
            }

            > div:first-child {
                height: 100%;

                @media (max-width: 768px) {
                    max-width: 310px;
                }
            }
        }
    `;
};

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector("html")?.setAttribute("style", "scrollbar-gutter: stable;");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector("html")?.removeAttribute("style");
};

export const showAllTabPagination = {
    cssOverrides: {
        perPageSelect: css`
            ${FormInputWrapper} {
                width: auto;
                ${IconWrapper} {
                    right: 0;
                }
            }
        `,
        pagination: css`
            gap: 0px 48px;
        `,
    },
} as PaginationPresentationProps;
